<form *ngIf="form" [formGroup]="form">
    <div class="form-group">
        <label>Bank Account Name:</label>
        <input [errorClassWhenInvalid]="form.controls['friendlyName']" placeholder="e.g. Operating Account, Savings, Money Market" name="account-name" class="form-control form-control-sm" formControlName="friendlyName">
        <lf-control-messages [control]="form.controls['friendlyName']"></lf-control-messages>
    </div>

    <small class="text-danger" *ngIf="updateAccount?.depositBankAccount && jwt.superUser">
        This account is already connected to Stripe. Use caution if updating banking details.
        <app-super-star></app-super-star>
    </small>

    <div class="form-group">
        <label>Bank Routing Number:</label>
        <input [matTooltip]="'Account and routing numbers cannot be updated after enabling deposits.'" [matTooltipDisabled]="!updateAccount?.depositBankAccount || jwt.superUser" [errorClassWhenInvalid]="form.controls['routingNumber']" name="routing-number" class="form-control form-control-sm" formControlName="routingNumber" minlength="9" maxlength="9">
        <lf-control-messages [control]="form.controls['routingNumber']"></lf-control-messages>
    </div>
    <ng-container *ngIf="!updateAccount?.depositBankAccount || jwt.superUser">
        <div class="form-group">
            <label>Bank Account Number:</label>
            <input [errorClassWhenInvalid]="form.controls['accountNumber']" name="account-number" class="form-control form-control-sm" formControlName="accountNumber">
            <small class="text-muted d-block" *ngIf="updateAccount?.last4">Current account number hidden for security.
                Leave
                this field
                blank to keep the old value.</small>
            <lf-control-messages [control]="form.controls['accountNumber']"></lf-control-messages>
        </div>
    </ng-container>
    <div class="form-group d-flex">
        <mat-slide-toggle
            #depositToggle
            (change)="isValid(false)"
            class="mr-2"
            formControlName="useForDeposits">
        </mat-slide-toggle>
        <label (click)="depositToggle.toggle(); isValid(false)" [attr.aria-label]="'Toggle deposit account'">
            Save as a deposit destination for online payments from homeowners.</label>
    </div>
    <ng-container *ngIf="!depositToggle.checked && (updateAccount?.depositBankAccount)">
        <div class="form-group" *ngIf="replacementCandidates.length > 0">
            <label>Select a replacement deposit account for outstanding charges:</label>
            <mat-form-field appearance="outline" class="w-100 bg-white">
                <mat-select formControlName="replacementDepositAccount" placeholder="Select Replacement Account">
                    <ng-container *ngFor="let account of replacementCandidates">
                        <mat-option [value]="account.id">
                            {{ account.friendlyName }}
                        </mat-option>
                    </ng-container>
                </mat-select>
            </mat-form-field>

            <lf-control-messages [control]="form.controls['replacementDepositAccount']"></lf-control-messages>
        </div>
        <div *ngIf="replacementCandidates.length === 0" class="mb-4">
            <p class="text-danger">
                This is your last deposit account. You must have at least one deposit account on file to make use of the
                site's features.
            </p>
            <p>
                Removing your last deposit account would leave nowhere for your users' payments to go. If you wish to
                cancel your account with us, please contact our Support Team. Otherwise, please add a new deposit
                account before returning to remove this one.
            </p>
        </div>
    </ng-container>


    <ng-template #allianceTxLink>
        <div class="form-group d-flex">
            <mat-slide-toggle
                #transactionToggle
                class="mr-2"
                formControlName="allianceTransactionImportEnabled">
            </mat-slide-toggle>
            <label (click)="transactionToggle.toggle();" [attr.aria-label]="'Import transactions from your bank'">Import
                transactions from Alliance Bank's daily feed.</label>
        </div>
        <div class="form-group" *ngIf="transactionToggle.checked">
            <label>
                Import transactions after:
            </label>
            <input
                (click)="startDate.open()"
                [matDatepicker]="startDate"
                class="form-control date d-inline-block"
                formControlName="allianceTransactionImportAfter"
                placeholder="Start date..."
            />
            <mat-datepicker #startDate></mat-datepicker>
        </div>
    </ng-template>
    <ng-container *ngIf="!(updateAccount?.isAllianceAccount || form.get('routingNumber').value === '122105980') else allianceTxLink">
        <div class="form-group d-flex">
            <mat-slide-toggle
                #transactionToggle
                (change)="launchPlaidIfNoTokens($event.checked)"
                class="mr-2"
                formControlName="connectToTransactionFeed">
            </mat-slide-toggle>
            <label (click)="transactionToggle.toggle(); launchPlaidIfNoTokens(transactionToggle.checked)" [attr.aria-label]="'Import transactions from your bank'">Import
                transactions from your bank.</label>
        </div>
        <ng-container *ngIf="transactionToggle.checked">
            <ng-container *ngIf="allTokens.length">
                <div class="form-group mb-0">
                    <label>Select Bank Login</label>
                    <mat-form-field appearance="outline" class="w-100 bg-white">
                        <mat-select (selectionChange)="searchIfNew()" formControlName="plaidToken" placeholder="Select an Existing Login" [compareWith]="compareTokens">
                            <ng-container *ngFor="let token of allTokens">
                                <mat-option [attr.aria-label]="token.institution.name + ' - ' + token.plaidError ? 'Needs Attention' : token.plaidAccounts.length + ' Accounts'" [value]="token">
                                    {{ token.propertyManagementCompanyId ? 'PMC: ' : '' }}
                                    {{ token.institution.name }}
                                    - {{ token.plaidError ? 'Needs Attention' : token.plaidAccounts.length + ' Accounts' }}
                                </mat-option>
                            </ng-container>
                            <mat-option [attr.aria-label]="'New Login'" [value]="'new-login'">
                                Add new Bank Login
                            </mat-option>
                        </mat-select>
                    </mat-form-field>

                    <ng-container *ngIf="form.get('plaidToken').value">

                        <ng-container *ngIf="!form.get('plaidToken').value.plaidError else error">
                            <label class="mt-2">Select Account</label>
                            <mat-form-field appearance="outline" class="w-100 bg-white">
                                <mat-select (selectionChange)="updateIfNew()" formControlName="plaidAccount" placeholder="Select an Existing Account" [compareWith]="compareAccounts">
                                    <ng-container *ngFor="let account of form.get('plaidToken').value.plaidAccounts">
                                        <mat-option [attr.aria-label]="account.name" [disabled]="account.unifiedBankAccount && account.unifiedBankAccount.id !== updateAccount?.id" [value]="account">
                                            {{ account.name }} - ****{{ account.mask }}
                                        </mat-option>
                                    </ng-container>
                                    <mat-option [attr.aria-label]="'New Account'" [value]="'new-account'">
                                        Connect Another Account
                                    </mat-option>
                                </mat-select>
                            </mat-form-field>
                        </ng-container>

                        <ng-template #error>
                            This login requires attention. Please click
                            <a (click)="updateToken(form.get('plaidToken').value)">here</a> to repair the connection.
                        </ng-template>
                    </ng-container>
                </div>

            </ng-container>
        </ng-container>

        <div class="form-group" *ngIf="transactionToggle.checked">
            <label class="mt-2">
                Import transactions after:
            </label>
            <input
                (click)="startDate.open()"
                [matDatepicker]="startDate"
                class="form-control date d-inline-block"
                formControlName="plaidImportAfter"
                placeholder="Start date..."
            />
            <mat-datepicker #startDate></mat-datepicker>
        </div>
    </ng-container>
</form>
